import moment from 'moment-timezone';
import store from '@/store';
import { hasProductRedemptionsRemaining } from '@/utils/redemptions';

const ticketHelpers = {
  activeTickets: product =>
    product.order_products
      .filter(op => ticketHelpers.isActive(op, product))
      .sort(({ start_date: a }, { start_date: b }) => a - b)
      .sort(({ redeemed: a }, { redeemed: b }) => moment(b) - moment(a))
      .sort(({ ticket: { timer: a } }, { ticket: { timer: b } }) => a - b),
  isActive: (op, product) =>
    !ticketHelpers.schedulePending(op) &&
    !ticketHelpers.hideTicketOnMobile(product) &&
    (!ticketHelpers.redeemOnMobile(product) ||
      ticketHelpers.ticketShows(product) === 'immediate' ||
      (!!op.redeemed &&
        (!product?.ticket_settings?.hide_ticket?.after_redemption?.type ||
          (!!product?.ticket_settings?.hide_ticket?.after_redemption?.type &&
            op.ticket.timer > 0)))),
  endDate: op =>
    op?.end_date
      ? moment.utc(op.end_date).local()
      : moment('3000-01-01 00:00:00'),

  startDate: op =>
    // eslint-disable-next-line no-nested-ternary
    op?.start_date
      ? moment.utc(op.start_date).local()
      : op?.ticket?.start_date
        ? moment.utc(op.ticket.start_date).local()
        : moment('2000-01-01 00:00:00'),

  hasAttemptedQuiz: product =>
    product.settings.quiz.remaining_attempts <
    product.settings.quiz.max_attempts,

  getBlackoutDates: (product, deal = null) => {
    const productDates = product?.redemption_blackout_dates;
    const dealDates = deal?.redemption_blackout_dates;
    const productArr = Array.isArray(productDates) ? productDates : [];
    const dealArr = Array.isArray(dealDates) ? dealDates : [];
    return [...productArr, ...dealArr];
  },

  hasBlackoutDates: (product, deal = null) =>
    ticketHelpers.getBlackoutDates(product, deal).length > 0,

  // `at` (optional moment) lets callers test a future moment; defaults to now.
  isInBlackoutRange: (blackoutDate, at = null) => {
    if (blackoutDate.type === 'recurring') {
      return ticketHelpers.isRecurringBlackoutActive(blackoutDate, at);
    }

    const ref = at || moment();
    const startDate = moment.utc(blackoutDate.start_date);
    const endDate = blackoutDate.end_date
      ? moment.utc(blackoutDate.end_date)
      : null;
    if (!endDate)
      return ref.isAfter(startDate) || ref.isSame(startDate);

    // end_date is exclusive (mirrors the PHP gate's `end_date > now`), so the
    // activity is available again at exactly end_date.
    return ref.isBetween(startDate, endDate, undefined, '[)');
  },

  // Mirror of App\Product::isRecurringBlackoutActive (backend is authoritative).
  // Recurring rules store partner-local wall-clock values, so we evaluate in the
  // partner's timezone. Day-of-week: 0=Sun..6=Sat (moment().day()). `at` (optional)
  // tests a specific moment instead of now (used by the next-available scan).
  isRecurringBlackoutActive: (rule, at = null) => {
    const tz = store.state.partner?.timezone;
    const now = at ? at.clone() : tz ? moment.tz(tz) : moment();
    const date = now.format('YYYY-MM-DD');

    // Date bounds (partner-local ISO dates compare lexicographically).
    if (rule.starts_on && date < rule.starts_on) return false;
    if (rule.ends_on && date > rule.ends_on) return false;

    // Frequency / day match.
    const frequency = rule.frequency || 'weekly';
    if (frequency === 'weekly') {
      const days = (rule.days_of_week || []).map(d => parseInt(d, 10));
      if (!days.includes(now.day())) return false;
    } else if (frequency === 'monthly') {
      // Clamp to the month's length so a rule on the 29th/30th/31st still fires
      // (on the last day) in shorter months instead of silently skipping.
      const targetDay = Math.min(parseInt(rule.day_of_month, 10) || 0, now.daysInMonth());
      if (now.date() !== targetDay) return false;
    }
    // 'daily' matches every day within the date bounds.

    // No time window means the entire matching day is blacked out.
    if (!rule.start_time && !rule.end_time) return true;

    const nowMinutes = now.hours() * 60 + now.minutes();
    const startMinutes = ticketHelpers.blackoutTimeToMinutes(rule.start_time);
    const endMinutes = ticketHelpers.blackoutTimeToMinutes(rule.end_time);

    // The end time is exclusive: a "5:00 PM–6:00 PM" window blocks [17:00, 18:00)
    // and the activity is available again at exactly 6:00 PM (not 6:01).
    return nowMinutes >= startMinutes && nowMinutes < endMinutes;
  },

  blackoutTimeToMinutes: time => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':');
    return parseInt(hours, 10) * 60 + (parseInt(minutes, 10) || 0);
  },

  // The message shown when an activity is blacked out, as { message, lines }.
  // Partner override (product.settings.blackout_message) wins, otherwise
  // "Not valid on:" followed by one line per blackout date, range or rule.
  blackoutUnavailableContent: (product, deal = null) => {
    const override = product?.settings?.blackout_message;
    if (override && String(override).trim() !== '') {
      return { message: override, lines: [] };
    }

    const lines = ticketHelpers.blackoutScheduleLines(
      ticketHelpers.getBlackoutDates(product, deal)
    );
    return lines.length
      ? { message: 'Not valid on:', lines }
      : { message: 'This item is currently unavailable.', lines: [] };
  },

  // One phrase per blackout entry, in entry order
  blackoutScheduleLines: entries => {
    const phrases = [];
    const weeklyGroups = {}; // window signature => { index, days: [] }
    (entries || []).forEach(entry => {
      if (!entry) return;
      const isRecurring = entry.type === 'recurring';

      // Merge weekly recurring rules sharing the same time window so a day
      // blacked out by two rules (e.g. Thursday) isn't listed twice.
      if (isRecurring && (entry.frequency || 'weekly') === 'weekly') {
        const window = ticketHelpers.recurringWindow(entry);
        const days = (entry.days_of_week || []).map(d => parseInt(d, 10));
        if (weeklyGroups[window]) {
          weeklyGroups[window].days.push(...days);
        } else {
          weeklyGroups[window] = { index: phrases.length, days };
          phrases.push(null);
        }
        return;
      }

      let phrase = '';
      if (isRecurring) {
        phrase = ticketHelpers.recurringSchedulePhrase(entry);
      } else if (entry.start_date || entry.end_date) {
        phrase = ticketHelpers.rangeSchedulePhrase(entry);
      }
      if (phrase) phrases.push(phrase);
    });
    Object.keys(weeklyGroups).forEach(window => {
      const g = weeklyGroups[window];
      phrases[g.index] = ticketHelpers.weeklyDaysPhrase(g.days, window);
    });
    return phrases.filter(p => p !== null && p !== '');
  },

  // "g:i A–g:i A" window for a recurring rule, or '' when all-day. Also the
  // merge key for weekly rules in blackoutScheduleLines.
  recurringWindow: rule =>
    rule.start_time && rule.end_time
      ? `${ticketHelpers.formatClock(rule.start_time)}–${ticketHelpers.formatClock(rule.end_time)}`
      : '',

  recurringSchedulePhrase: rule => {
    const frequency = rule.frequency || 'weekly';
    const window = ticketHelpers.recurringWindow(rule);
    if (frequency === 'daily') {
      return window ? `Daily ${window}` : 'Daily';
    }
    if (frequency === 'monthly') {
      const label = `The ${ticketHelpers.ordinalDay(parseInt(rule.day_of_month, 10) || 1)} of each month`;
      return window ? `${label} ${window}` : label;
    }
    return ticketHelpers.weeklyDaysPhrase(rule.days_of_week || [], window);
  },

  // "Mondays, Tuesdays and Thursdays" (Monday-first, deduped), optional window.
  weeklyDaysPhrase: (dayNumbers, window = '') => {
    const names = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
    const unique = [...new Set((dayNumbers || []).map(d => parseInt(d, 10)))];
    unique.sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)); // Monday-first
    const days = unique.map(d => names[d]).filter(Boolean);
    const label = days.length ? ticketHelpers.joinWithAnd(days) : 'Weekly';
    return window ? `${label} ${window}` : label;
  },

  rangeSchedulePhrase: entry => {
    const tz = store.state.partner?.timezone;
    const toLocal = value => (tz ? moment.utc(value).tz(tz) : moment.utc(value).local());
    const start = entry.start_date ? toLocal(entry.start_date) : null;
    const end = entry.end_date ? toLocal(entry.end_date) : null;

    if (start && end) {
      const fullDay = start.format('HH:mm') === '00:00' && end.format('HH:mm') === '23:59';
      if (start.isSame(end, 'day')) {
        return fullDay
          ? start.format('MMMM D, YYYY')
          : `${start.format('MMMM D, YYYY')} ${start.format('h:mm A')}–${end.format('h:mm A')}`;
      }
      return `${start.format('MMMM D, YYYY')} – ${end.format('MMMM D, YYYY')}`;
    }
    if (start) return `From ${start.format('MMMM D, YYYY')}`;
    if (end) return `Until ${end.format('MMMM D, YYYY')}`;
    return '';
  },

  formatClock: time => {
    const [hours, minutes] = time.split(':');
    return moment()
      .hours(parseInt(hours, 10))
      .minutes(parseInt(minutes, 10) || 0)
      .format('h:mm A');
  },

  joinWithAnd: items => {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  },

  ordinalDay: day => {
    if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`;
    return day + ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
  },

  hasEnded: op => moment().isAfter(moment(ticketHelpers.endDate(op))),

  hasProductRedemptionsRemaining,

  hasQuizAttemptedRemaining: product =>
    !!product.settings?.quiz?.remaining_attempts,

  hasRedemptionHistory: ({ order_products: orderProducts = false }) =>
    orderProducts && !!orderProducts.filter(op => op.redeemed).length,

  hasStarted: (op, product) => {
    const hourSpecific =
      product.redemption_policy?.activation_type === 'date_time';
    return hourSpecific
      ? moment().isAfter(moment(ticketHelpers.startDate(op)))
      : moment().isAfter(moment(ticketHelpers.startDate(op).startOf('day')));
  },

  htmlTicketIsPromoCode: (op, product) => {
    const isHtmlTicket = op.type === 'html';
    const hasHtmlCode = /href="/.test(op.value) && /promo code/.test(op.value);
    const redeemOnline = product?.redemption_settings?.mode === 'Online';
    return hasHtmlCode && isHtmlTicket && redeemOnline;
  },

  htmlTicketToPromoCode: op => {
    const regex =
      /.+(?=href)href="([^"]+).+(?=promo code:\s*)promo code:\s*([^.]+).+/g;
    const matches = op.value.match(regex);

    return {
      website: matches[0],
      promo_code: matches[1]
    };
  },

  isSelectable: (op, product, deal = null) =>
    ticketHelpers.hasStarted(op, product) &&
    !ticketHelpers.isExpired(op) &&
    !ticketHelpers.isBlackedOut(op, product, deal) &&
    !ticketHelpers.isRedeemed(op),

  isAvailable: (op, product, deal = null) =>
    !ticketHelpers.isRedeemed(op) &&
    ticketHelpers.hasStarted(op, product) &&
    !ticketHelpers.hasEnded(op) &&
    !ticketHelpers.isBlackedOut(op, product, deal),

  isBlackedOut: (op, product, deal = null) => {
    if (
      !ticketHelpers.hasStarted(op, product) ||
      ticketHelpers.hasEnded(op) ||
      !ticketHelpers.hasBlackoutDates(product, deal)
    )
      return false;
    const blackoutDates = ticketHelpers.getBlackoutDates(product, deal);
    // Wrap in an arrow: `.some` passes (element, index, array), and the index
    // would land in isInBlackoutRange's optional `at` arg and break evaluation.
    return blackoutDates.some(b => ticketHelpers.isInBlackoutRange(b));
  },


  earliestAvailableMonth: (availableDates = []) => {
    const days = (availableDates || [])
      .map(({ date } = {}) => String(date ?? '').slice(0, 10))
      .filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date));

    if (!days.length) return null;
    const [year, month] = days.reduce((a, b) => (a < b ? a : b)).split('-');

    return { month: Number(month) - 1, year: Number(year) };
  },

  isDateSpecific: product => {
    if (!product.redemption_policy?.activation_type) return false;
    return [
      'date',
      'date_time',
      'selected_date',
      'selected_date_time'
    ].includes(product.redemption_policy.activation_type);
  },

  isExpired: op => !op.redeemed && moment().isAfter(ticketHelpers.endDate(op)),

  isQuiz: p => p?.type === 'quiz',

  isQuizCoolingDown: (op, p) =>
    ticketHelpers.isQuiz(p) &&
    !ticketHelpers.isRedeemed(op) &&
    ticketHelpers.hasAttemptedQuiz(p) &&
    ticketHelpers.nextQuizAttemptMs(p) > 0 &&
    ticketHelpers.remainingAttempts(p) > 0,

  isRedeemed: op => !!op.redeemed,

  // An auto-redeemed in-pass booking can be redeemed for a future date/time.
  // Until that moment is reached the ticket is booked, not yet used.
  isFutureRedemption: op =>
    !!op.redeemed && moment.utc(op.redeemed).isAfter(moment()),

  isRepeatable: product => !!product?.repeat,

  isTimedOut: op => {
    const hasTimer = !!((op || {}).ticket || {}).timer;
    return hasTimer ? op.ticket.timer <= 0 : false;
  },

  lastRedemption: ({ order_products: orderProducts = false }) => {
    if (!orderProducts) return null;

    const idx = orderProducts.findIndex(op => op.redeemed);
    return orderProducts[idx];
  },

  // First moment the activity is available again — found by walking forward from
  // "now", jumping past each active blackout's current occurrence until a free
  // moment is reached. Handles all-day recurring (e.g. every Mon/Thu -> next free
  // day), time windows, and one-off ranges. Null if open-ended / unbounded.
  nextAvailableDate: (op, product, deal = null) => {
    if (!ticketHelpers.isBlackedOut(op, product, deal)) return null;
    return ticketHelpers.nextAvailableMoment(product, deal);
  },

  // Product-level forward scan (no order product needed): the first moment the
  // product's blackout windows all clear. Null if open-ended / unbounded. Callers
  // decide whether the product is currently blocked before showing this.
  nextAvailableMoment: (product, deal = null) => {
    const tz = store.state.partner?.timezone;
    const blackouts = ticketHelpers.getBlackoutDates(product, deal);
    let cursor = tz ? moment.tz(tz) : moment();

    // Bounded walk (~2 years of day/window boundaries) so we never loop forever.
    for (let i = 0; i < 800; i += 1) {
      const active = blackouts.filter(b => ticketHelpers.isInBlackoutRange(b, cursor));
      if (!active.length) return cursor;

      let next = null;
      for (const blackout of active) {
        const end = ticketHelpers.blackoutOccurrenceEnd(blackout, cursor, tz);
        if (!end) return null; // open-ended -> unavailable indefinitely
        if (!next || end.isAfter(next)) next = end;
      }
      if (!next || !next.isAfter(cursor)) return null; // safety
      cursor = next;
    }
    return null;
  },

  // Order-product-free variant for list rows: is the product inside an active
  // blackout window right now (regardless of any single ticket's start/end)?
  isProductBlackedOutNow: (product, deal = null) =>
    ticketHelpers.hasBlackoutDates(product, deal) &&
    ticketHelpers
      .getBlackoutDates(product, deal)
      .some(b => ticketHelpers.isInBlackoutRange(b)),

  // Short caption for list rows: "Currently unavailable", plus the next-available
  // date when known and the partner hasn't hidden it. The year is dropped for dense
  // list rows, but the time always shows: an all-day recurring rule releases at
  // midnight, and hiding that clock made weekly/monthly rows read as a bare date
  // while every other blackout type showed a time (and the detail card showed
  // "12:00 AM" for the same rule).
  blackoutListCaption: (product, deal = null) => {
    const base = 'Currently unavailable';
    if (product?.settings?.blackout_show_next_available === false) return base;
    const next = ticketHelpers.nextAvailableMoment(product, deal);
    if (!next) return base;
    return `${base} · available ${next.format('MMM D, h:mm A')}`;
  },

  // The first moment the blackout occurrence covering `at` releases (its
  // exclusive end), in the partner timezone. Null for an open-ended one-off
  // range. This equals the configured end (end_time / end_date) exactly, so the
  // displayed "available again" time matches the gate's release moment.
  blackoutOccurrenceEnd: (blackout, at, tz) => {
    if (blackout.type === 'recurring') {
      // All-day: free at the start of the next day.
      if (!blackout.start_time && !blackout.end_time) {
        return at.clone().add(1, 'day').startOf('day');
      }
      const [hours, minutes] = blackout.end_time.split(':');
      let end = at
        .clone()
        .hours(parseInt(hours, 10))
        .minutes(parseInt(minutes, 10) || 0)
        .seconds(0)
        .milliseconds(0);
      // Window spans midnight and we're in the late-evening part -> ends tomorrow.
      if (!end.isAfter(at)) end = end.add(1, 'day');
      return end;
    }
    if (blackout.end_date) {
      return tz
        ? moment.utc(blackout.end_date).tz(tz)
        : moment.utc(blackout.end_date).local();
    }
    return null;
  },

  nextQuizAttempt: product =>
    ticketHelpers.isQuiz(product) && product?.settings?.quiz?.cool_down_time,

  nextQuizAttemptMs: product =>
    Math.max(
      0,
      ticketHelpers.isQuiz(product) &&
        moment(product.settings.quiz.cool_down_time).utc().valueOf() -
          moment.utc().valueOf()
    ),

  quizRemainingTime: timeObj => {
    const msDay = 86400000;
    const msHour = 3600000;

    if (timeObj.leftTime > msDay) {
      return moment()
        .add(timeObj.d, 'days')
        .add(timeObj.h, 'hours')
        .add(timeObj.m, 'minutes')
        .add(timeObj.s, 'seconds')
        .format('MMM Do, YYYY [at] h:mm A');
    }

    if (timeObj.leftTime > msHour) {
      const now = moment();
      const futureTime = now
        .clone()
        .add(timeObj.h, 'hours')
        .add(timeObj.m, 'minutes')
        .add(timeObj.s, 'seconds');
      if (futureTime.isAfter(now.clone().endOf('day')))
        return `Tomorrow at ${futureTime.format('h:mm A')}`;

      return `Today at ${futureTime.format('h:mm A')}`;
    }

    const mins = Number(timeObj.m);
    const secs = Number(timeObj.s);

    return `${mins > 0 ? `${mins}m ` : ''}${secs}s`;
  },

  hideTicketOnMobile: product =>
    !!product.ticket_settings.hide_ticket_on_mobile,

  redeemOnMobile: product => !!product.redeem_on_dashboard,

  remainingAttempts: product => product?.settings?.quiz?.remaining_attempts,

  decreaseAttempts: product => {
    const remaining = ticketHelpers.remainingAttempts(product) - 1;
    return !Number.isInteger(remaining) || remaining < 0 ? 0 : remaining;
  },

  repeatDate: op => op?.duplicate_date || null,

  repeatDateInFuture: op =>
    moment().isBefore(moment.utc(ticketHelpers.repeatDate(op)).local()),

  scheduledTickets: p =>
    p.order_products
      .filter(op => ticketHelpers.isScheduled(op, p))
      .sort(
        ({ start_date: a }, { start_date: b }) => new Date(a) - new Date(b)
      ),

  isScheduled: (op, p) =>
    !ticketHelpers.schedulePending(op) &&
    op.ticket &&
    !ticketHelpers.isActive(op, p) &&
    op.start_date &&
    moment.utc(op.start_date).isAfter(moment()),

  isBooked: op =>
    !ticketHelpers.schedulePending(op) &&
    !!op.ticket &&
    !ticketHelpers.isRedeemed(op) &&
    !!op.start_date &&
    moment.utc(op.start_date).isAfter(moment()),

  bookedTickets: ({ order_products: orderProducts = [] }) =>
    orderProducts
      .filter(ticketHelpers.isBooked)
      .sort(
        ({ start_date: a }, { start_date: b }) => new Date(a) - new Date(b)
      ),

  schedulePending: ({ ticket }) =>
    ticket && ticket.status === 'pending_date_selection',

  // The item is booked but the integration has not returned the voucher yet.
  voucherPending: (ticket = {}) => ticket?.status === 'pending_confirmation',

  schedulingRequired: ({ order_products: orderProducts = [] }) =>
    orderProducts.some(ticketHelpers.schedulePending),

  schedulingRequiredTickets: ({ order_products: orderProducts = [] }) =>
    orderProducts.filter(ticketHelpers.schedulePending),

  schedulingWithTimeRequired: ({ redemption_policy: redemptionPolicy }) =>
    redemptionPolicy?.activation_type === 'selected_date_time',

  ticketShows: product =>
    product.ticket_settings?.show_ticket?.type || 'redemption',

  ticketType: product => product?.order_products?.[0]?.ticket.type || 'none'
};

export default ticketHelpers;
